import axios, { AxiosInstance } from 'axios';

/**
 * Creates an Axios instance for a specific agent, with a request interceptor
 * that dynamically acquires and injects the Bearer token.
 *
 * @param baseURL - The agent's API base URL
 * @param getToken - Async function that returns a valid access token for this agent
 * @param onUnauthorized - Optional callback when a 401 response is received
 */
export function createAgentApiClient(
  baseURL: string,
  getToken: () => Promise<string>,
  onUnauthorized?: () => void,
): AxiosInstance {
  const client = axios.create({ baseURL });

  // Request interceptor: inject Bearer token
  client.interceptors.request.use(async (config) => {
    const token = await getToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Response interceptor: handle 401 Unauthorized
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        onUnauthorized?.();
      }
      return Promise.reject(error);
    },
  );

  return client;
}
